import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, Plus, X } from 'lucide-react';
import { RichTextField } from './RichTextField';
import { TextField } from './TextField';
import { TooltipListInput, type TooltipItem } from './TooltipListInput';
import { cn } from '@/lib/utils';

export interface ThematicSubsection {
  title?: string;
  content?: string;
  tooltips?: TooltipItem[];
}

interface ThematicSubsectionsInputProps {
  value?: ThematicSubsection[];
  onChange: (_sections: ThematicSubsection[]) => void;
}

const defaultSubsection = (): ThematicSubsection => ({
  title: '',
  content: '',
  tooltips: [],
});

export const ThematicSubsectionsInput: React.FC<ThematicSubsectionsInputProps> = ({
  value = [],
  onChange,
}) => {
  const [sections, setSections] = useState<ThematicSubsection[]>(value);
  const [openIndexes, setOpenIndexes] = useState<Set<number>>(new Set([0]));

  useEffect(() => {
    setSections(value);
  }, [value]);

  const addSection = useCallback(() => {
    const next = [...sections, defaultSubsection()];
    setSections(next);
    onChange(next);
    setOpenIndexes((prev) => new Set([...prev, next.length - 1]));
  }, [sections, onChange]);

  const removeSection = useCallback(
    (index: number) => {
      const next = sections.filter((_, i) => i !== index);
      setSections(next);
      onChange(next);
      setOpenIndexes((prev) => {
        const updated = new Set<number>();
        prev.forEach((i) => {
          if (i < index) updated.add(i);
          else if (i > index) updated.add(i - 1);
        });
        return updated;
      });
    },
    [sections, onChange]
  );

  const updateSection = useCallback(
    <K extends keyof ThematicSubsection>(index: number, field: K, fieldValue: ThematicSubsection[K]) => {
      const next = sections.map((s, i) =>
        i === index ? { ...s, [field]: fieldValue } : s
      );
      setSections(next);
      onChange(next);
    },
    [sections, onChange]
  );

  const toggleOpen = (index: number) => {
    setOpenIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  return (
    <div className="space-y-3">
      {sections.length === 0 ? (
        <div className="text-center py-6 text-gray-500 text-sm border-2 border-dashed border-gray-200 rounded-lg">
          Nenhuma seção adicionada.
          <br />
          <span className="text-xs text-gray-400">
            Adicione seções com título, texto e balões direcionais.
          </span>
        </div>
      ) : (
        sections.map((section, index) => {
          const isOpen = openIndexes.has(index);
          const headerLabel = section.title?.trim() || `Seção ${index + 1}`;
          return (
            <div
              key={index}
              className={cn(
                'border rounded-lg overflow-hidden',
                isOpen ? 'border-gray-300' : 'border-gray-200'
              )}
            >
              <button
                type="button"
                onClick={() => toggleOpen(index)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
              >
                <div className="flex items-center gap-2">
                  {isOpen ? (
                    <ChevronUp className="w-4 h-4 text-gray-500 shrink-0" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
                  )}
                  <span className="text-sm font-semibold text-gray-700 truncate max-w-[240px]">
                    {headerLabel}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSection(index);
                  }}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </button>

              {isOpen && (
                <div className="p-4 space-y-5 bg-white">
                  <TextField
                    label="Título da seção"
                    value={section.title ?? ''}
                    onChange={(v) => updateSection(index, 'title', v)}
                    placeholder="Ex: Dentro da 🏠"
                  />

                  <RichTextField
                    label="Conteúdo"
                    value={section.content ?? ''}
                    onChange={(v) => updateSection(index, 'content', v)}
                    placeholder="Texto da seção..."
                  />

                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Balões direcionais
                    </p>
                    <TooltipListInput
                      value={section.tooltips ?? []}
                      onChange={(v) => updateSection(index, 'tooltips', v)}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addSection}
        className="w-full gap-2"
      >
        <Plus className="w-4 h-4" />
        Adicionar seção
      </Button>
    </div>
  );
};
